import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import sellerApi from "@/lib/sellerApi";
import { Bell, ShoppingCart } from "lucide-react";

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = useCallback(() => {
    sellerApi.get("/seller/notifications/unread-count").then(({ data }) => setCount(data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try { const { data } = await sellerApi.get("/seller/notifications"); setItems(data); } catch { /* ignore */ }
    }
  };

  const markAll = async () => {
    await sellerApi.post("/seller/notifications/read-all").catch(() => {});
    setItems((l) => l.map((n) => ({ ...n, read: true })));
    setCount(0);
  };

  const openItem = async (n) => {
    if (!n.read) { await sellerApi.post(`/seller/notifications/${n.id}/read`).catch(() => {}); loadCount(); }
    setOpen(false);
    navigate("/seller/dashboard/orders");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-ink-soft transition-colors hover:text-brand" data-testid="seller-bell" aria-label="Notifications">
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="seller-bell-badge">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-white shadow-lift" data-testid="seller-notif-panel">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="font-semibold text-ink">Notifications</p>
            <button onClick={markAll} className="text-xs font-semibold text-brand hover:underline" data-testid="seller-notif-readall">Mark all read</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-muted">No notifications yet</p>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} className={`flex w-full items-start gap-3 border-b border-border/50 px-4 py-3 text-left last:border-0 hover:bg-cream-alt/50 ${!n.read ? "bg-brand/5" : ""}`} data-testid={`seller-notif-${n.id}`}>
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand"><ShoppingCart className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1 text-sm font-semibold text-ink">
                    {n.title}
                    {!n.read && <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />}
                  </span>
                  <span className="block text-xs text-ink-muted">{n.body}</span>
                  <span className="block text-[10px] text-ink-muted">{new Date(n.created_at).toLocaleString("en-IN")}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
